import { ReactNode } from "react";
import { PRODUCT_CONFIG } from "config/productConfig";
import { getProductFromSubdomain, PRODUCT_TYPES } from "utils/productDetection";
import { formatRegisteredSymbol } from "utils/textFormatting";

export type ProgressBarStep = {
    key: string;
    title: string;
};

export type ProgressBarConfig = {
    productName: ReactNode;
    currentStep: number;
    stepCompletion: readonly number[];
    steps: ProgressBarStep[];
};

// Progress bar steps for application flow
export const APPLICATION_STEPS: ProgressBarStep[] = [
    { title: "Personal Information", key: "contact" },
    { title: "Payment Information", key: "payment" },
    { title: "Review and Agree", key: "review" },
];

const dtoProductName = PRODUCT_CONFIG[PRODUCT_TYPES.DTO].productName;
const giwlProductName = PRODUCT_CONFIG[PRODUCT_TYPES.GIWL].productName;
const mltaProductName = formatRegisteredSymbol(PRODUCT_CONFIG[PRODUCT_TYPES.MLTA].productName);

// Progress bar configurations for each step
export const DTO_PROGRESS_CONFIGS = {
    contact: {
        productName: dtoProductName,
        currentStep: 0,
        stepCompletion: [0, 0, 0],
        steps: APPLICATION_STEPS,
    },
    payment: {
        productName: dtoProductName,
        currentStep: 1,
        stepCompletion: [100, 66, 0],
        steps: APPLICATION_STEPS,
    },
    review: {
        productName: dtoProductName,
        currentStep: 2,
        stepCompletion: [100, 100, 50],
        steps: APPLICATION_STEPS,
    },
} as const;

export const GIWL_PROGRESS_CONFIGS = {
    contact: {
        productName: giwlProductName,
        currentStep: 0,
        stepCompletion: [0, 0, 0],
        steps: APPLICATION_STEPS,
    },
    payment: {
        productName: giwlProductName,
        currentStep: 1,
        stepCompletion: [100, 66, 0],
        steps: APPLICATION_STEPS,
    },
    review: {
        productName: giwlProductName,
        currentStep: 2,
        stepCompletion: [100, 100, 50],
        steps: APPLICATION_STEPS,
    },
} as const;

export const MLTA_PROGRESS_CONFIGS = {
    contact: {
        productName: mltaProductName,
        currentStep: 0,
        stepCompletion: [0, 0, 0],
        steps: APPLICATION_STEPS,
    },
    payment: {
        productName: mltaProductName,
        currentStep: 1,
        stepCompletion: [100, 66, 0],
        steps: APPLICATION_STEPS,
    },
    review: {
        productName: mltaProductName,
        currentStep: 2,
        stepCompletion: [100, 100, 50],
        steps: APPLICATION_STEPS,
    },
} as const;

/**
 * Gets the progress bar configuration for the current step, choosing the
 * product-specific configs based on the active subdomain.
 * @param step The current step in the application flow
 * @returns The progress bar configuration for the given step
 */
export function getProgressConfig(step: string): ProgressBarConfig {
    const productType = getProductFromSubdomain();

    switch (productType) {
        case PRODUCT_TYPES.MLTA:
            return MLTA_PROGRESS_CONFIGS[step as keyof typeof MLTA_PROGRESS_CONFIGS];
        case PRODUCT_TYPES.GIWL:
            return GIWL_PROGRESS_CONFIGS[step as keyof typeof GIWL_PROGRESS_CONFIGS];
        default:
            return DTO_PROGRESS_CONFIGS[step as keyof typeof DTO_PROGRESS_CONFIGS];
    }
}

/**
 * Determines the current progress step from a pathname
 * @param pathname The current route pathname
 * @returns The progress step key or undefined if not in application flow
 */
export function getProgressStepFromPathname(pathname: string): string | undefined {
    if (pathname.includes("/application/contact")) {
        return "contact";
    }
    if (pathname.includes("/application/payment")) {
        return "payment";
    }
    if (pathname.includes("/application/review")) {
        return "review";
    }
    return undefined;
}
